import * as i0 from '@angular/core';
import { InjectionToken, Inject, Optional, Injectable, inject, NgModule } from '@angular/core';
import { BehaviorSubject, firstValueFrom, catchError, throwError } from 'rxjs';
import * as i1 from '@angular/common/http';
import { HttpErrorResponse, HttpHeaders, HTTP_INTERCEPTORS } from '@angular/common/http';
import * as i2 from '@angular/router';
const ERROR_AUTHENTICATION_NOT_INITALIZED = 'FEHLER: Authentifizierung nicht initialisiert. initializeAuthentication() aufrufen.';
var InitializationStatus;
(function (InitializationStatus) {
  InitializationStatus["NONE"] = "NONE";
  InitializationStatus["INITIALIZING"] = "INITIALIZING";
  InitializationStatus["SUCCESSFUL"] = "SUCCESSFUL";
  InitializationStatus["FAILED"] = "FAILED";
})(InitializationStatus || (InitializationStatus = {}));
var OtpConfiguration;
(function (OtpConfiguration) {
  OtpConfiguration["NONE"] = "NONE";
  OtpConfiguration["OPTIONAL"] = "OPTIONAL";
  OtpConfiguration["REQUIRED"] = "REQUIRED";
})(OtpConfiguration || (OtpConfiguration = {}));
const ACTIVE_ROUTE_KEY = 'ACTIVE_ROUTE_KEY';
const BASE_URL = new InjectionToken('BASE_URL');
const DEFAULT_ROUTE = new InjectionToken('DEFAULT_ROUTE');
const INITIALIZATION_TIMEOUT = new InjectionToken('INITIALIZATION_TIMEOUT');
const INVALIDATE_USER_ON_SESSION_ERROR = new InjectionToken('INVALIDATE_USER_ON_SESSION_ERROR');
const LOGIN_ROUTE = new InjectionToken('LOGIN_ROUTE');
const POST_HEADERS = new InjectionToken('POST_HEADERS');
const PUBLIC_ROUTES = new InjectionToken('PUBLIC_ROUTES');
function ensurePromise(value, fallbackValue) {
  if (typeof value === 'undefined') {
    return Promise.resolve(fallbackValue);
  }
  return Promise.resolve(value);
}
class TimeoutError extends Error {
  constructor(message) {
    super(message);
  }
}
function withTimeout(value, timeout, error) {
  if (!error) {
    error = new TimeoutError('TIMEOUT');
  }
  const timer = new Promise((_, reject) => setTimeout(() => reject(error), timeout));
  return Promise.race([value, timer]);
}
function ensureEndsWith(value = '', endsWith, caseSensitive = true) {
  if (caseSensitive) {
    return value.endsWith(endsWith) ? value : value + endsWith;
  } else {
    return value.toLocaleLowerCase().endsWith(endsWith.toLocaleLowerCase()) ? value : value + endsWith;
  }
}
function ensureStartsWith(value, startsWith, caseSensitive = true) {
  if (caseSensitive) {
    return value.startsWith(startsWith) ? value : startsWith + value;
  } else {
    return value.toLocaleLowerCase().startsWith(startsWith.toLocaleLowerCase()) ? value : startsWith + value;
  }
}
function getItem(key, defaultValue) {
  const item = localStorage.getItem(key);
  if (item === null) {
    return defaultValue ?? null;
  }
  return JSON.parse(item);
}
function removeItem(key) {
  localStorage.removeItem(key);
}
function setItem(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}
const WINDOW = new InjectionToken('WINDOW');
class AuthenticationService {
  constructor(httpd, router, window, postHeaders, baseUrlGetter, defaultRouteGetter, loginRouteGetter, publicRoutesGetter, timeoutGetter, invalidateUserOnSessionErrorGetter) {
    this.httpd = httpd;
    this.router = router;
    this.window = window;
    this.postHeaders = postHeaders;
    this.baseUrlGetter = baseUrlGetter;
    this.defaultRouteGetter = defaultRouteGetter;
    this.loginRouteGetter = loginRouteGetter;
    this.publicRoutesGetter = publicRoutesGetter;
    this.timeoutGetter = timeoutGetter;
    this.invalidateUserOnSessionErrorGetter = invalidateUserOnSessionErrorGetter;
    this.authProvidersValue = null;
    this.baseUrl = '/';
    this.defaultRoute = '/';
    this.isInitialized = false;
    this.initializationTimeout = 5000;
    this.initializing = false;
    this.invalidateUserOnSessionError = false;
    this.loadProfileFrom = null;
    this.loginRoute = '/login';
    this.publicRoutes = [];
    this.userDataValue = null;
    this.authProviderStore = new BehaviorSubject(null);
    this.initializationStatusStore = new BehaviorSubject(InitializationStatus.NONE);
    this.userDataStore = new BehaviorSubject(null);
    this.authProviders = this.authProviderStore.asObservable();
    this.initializationStatus = this.initializationStatusStore.asObservable();
    this.userData = this.userDataStore.asObservable();
    this.authProviderStore.subscribe(item => this.authProvidersValue = item);
    this.userDataStore.subscribe(item => this.userDataValue = item);
  }
  async initializeAuthentication(options = {}) {
    if (this.isInitialized || this.initializing) {
      return;
    }
    this.initializing = true;
    this.initializationStatusStore.next(InitializationStatus.INITIALIZING);
    this.baseUrl = await ensurePromise(this.baseUrlGetter, '/');
    this.initializationTimeout = await ensurePromise(this.timeoutGetter, 5000);
    this.invalidateUserOnSessionError = await ensurePromise(this.invalidateUserOnSessionErrorGetter, false);
    const defaultRoute = await ensurePromise(this.defaultRouteGetter, this.defaultRoute);
    this.defaultRoute = ensureStartsWith(defaultRoute, '/');
    const loginRoutePromise = ensurePromise(this.loginRouteGetter, this.loginRoute);
    const publicRoutesPromise = ensurePromise(this.publicRoutesGetter, this.publicRoutes);
    const loadProfilePromise = ensurePromise(options.loadProfileFrom, null);
    const results = await Promise.all([loginRoutePromise, publicRoutesPromise, loadProfilePromise]);
    this.loginRoute = ensureStartsWith(results[0], '/');
    this.publicRoutes = results[1].map(route => ensureStartsWith(route, '/'));
    this.loadProfileFrom = results[2];
    this.initHeaders = options.headers;
    try {
      await this.loadAuthProviders();
    } catch (error) {
      if (error instanceof TimeoutError) {
        this.handleTimeoutError();
      } else if (error instanceof HttpErrorResponse) {
        this.handleHttpErrorResponse(error);
      }
      throw error;
    }
    try {
      await this.loadUserData();
    } catch (error) {
      if (error instanceof TimeoutError) {
        this.handleTimeoutError();
        throw error;
      } else if (error instanceof HttpErrorResponse) {
        // 401 UNAUTHORIZED beim Laden der Nutzerdaten ist kein Fehler; es liegt schlicht keine aktive Anmeldung vor.
        if (error.status !== 401) {
          this.handleHttpErrorResponse(error);
          throw error;
        }
      } else {
        throw error;
      }
    }
    this.isInitialized = true;
    this.initializationStatusStore.next(InitializationStatus.SUCCESSFUL);
    if (this.userDataValue) {
      this.tryRedirectToStoredRoute();
    }
    this.initializing = false;
  }
  tryRedirectToStoredRoute() {
    const activeRoute = this.router.url;
    const storedRoute = getItem(ACTIVE_ROUTE_KEY, null);
    let target;
    if (this.publicRoutes.some(route => activeRoute.startsWith(route))) {
      target = activeRoute;
    } else {
      target = this.defaultRoute;
    }
    if (storedRoute) {
      removeItem(ACTIVE_ROUTE_KEY);
      if (!storedRoute.startsWith(this.loginRoute)) {
        target = storedRoute;
      }
    }
    setTimeout(() => {
      this.router.navigateByUrl(target, {
        replaceUrl: true
      });
    });
  }
  handleHttpErrorResponse(error) {
    const {
      status,
      statusText
    } = error;
    if (status === 0) {
      console.log(`Fehler beim Initialisieren der Authentifizierung: Server nicht erreichbar oder reagiert nicht.`);
    } else {
      console.log(`Fehler beim Initialisieren der Authentifizierung: Status ${status}, Meldung ${statusText}`);
    }
    this.initializing = false;
    this.initializationStatusStore.next(InitializationStatus.FAILED);
  }
  handleTimeoutError() {
    console.log(`Fehler beim Initialisieren der Authentifizierung: Server nicht erreichbar oder reagiert nicht.`);
    this.initializing = false;
    this.initializationStatusStore.next(InitializationStatus.FAILED);
  }
  isAuthenticated(route) {
    const baseURI = this.window.document.head.baseURI;
    const fullLoginRoute = new URL(this.loginRoute, baseURI).toString();
    const startsWithLoginRoute = this.window.location.href.startsWith(fullLoginRoute);
    if (!this.userDataValue && !startsWithLoginRoute) {
      const {
        protocol,
        host,
        pathname
      } = this.window.location;
      let currentUrl = `${protocol}//${host}${pathname}`.substring(baseURI.length);
      currentUrl = ensureStartsWith(currentUrl, '/');
      const queryParam = route.queryParamMap;
      if (queryParam.keys.length > 0) {
        currentUrl += '?' + queryParam.keys.map(key => queryParam.getAll(key).map(value => key + '=' + value).join('&')).join('&');
      }
      this.storeActiveRoute(currentUrl);
      return this.router.parseUrl(this.loginRoute);
    }
    return !!this.userDataValue || startsWithLoginRoute;
  }
  async login(username, password, otp) {
    if (!this.isInitialized) {
      return Promise.reject(ERROR_AUTHENTICATION_NOT_INITALIZED);
    }
    if (!this.authProvidersValue?.formLogin) {
      return Promise.resolve(null);
    }
    const body = new FormData();
    body.append('username', username);
    body.append('password', password);
    if (otp) {
      body.append('otp', otp);
    }
    const headers = new HttpHeaders({
      ...this.postHeaders,
      'X-Requested-With': 'XMLHttpRequest'
    });
    return firstValueFrom(this.httpd.post(this.authProvidersValue.formLogin, body, {
      headers
    })).then(() => this.loadUserData()).then(success => {
      if (success) {
        this.tryRedirectToStoredRoute();
      }
      return success;
    }).catch(error => error.status === 401 ? Promise.resolve(false) : Promise.reject(error));
  }
  async logout() {
    if (!this.isInitialized) {
      return Promise.reject(ERROR_AUTHENTICATION_NOT_INITALIZED);
    }
    this.userDataStore.next(null);
    removeItem(ACTIVE_ROUTE_KEY);
    if (this.authProvidersValue?.logout) {
      const headers = new HttpHeaders({
        ...this.postHeaders,
        'X-Requested-With': 'XMLHttpRequest'
      });
      // leeres subscribe(), um den konfigurierten Request (ein **cold** observable) zu triggern.
      const request = this.httpd.post(this.authProvidersValue.logout, null, {
        headers
      });
      return firstValueFrom(request);
    }
    return Promise.resolve();
  }
  invalidateUser(reason) {
    if (typeof this.invalidateUserOnSessionError === 'boolean' && this.invalidateUserOnSessionError) {
      this.userDataStore.next(null);
    } else if (typeof reason === 'number' && Array.isArray(this.invalidateUserOnSessionError) && this.invalidateUserOnSessionError.includes(reason)) {
      this.userDataStore.next(null);
    }
  }
  resetAuthentication() {
    this.authProviderStore.next(null);
    this.userDataStore.next(null);
    this.isInitialized = false;
    this.initializationStatusStore.next(InitializationStatus.NONE);
    removeItem(ACTIVE_ROUTE_KEY);
  }
  storeActiveRoute(value) {
    if (!value || value === this.defaultRoute || value.startsWith(this.loginRoute)) {
      return;
    }
    setItem(ACTIVE_ROUTE_KEY, value);
  }
  async loadAuthProviders() {
    const promise = firstValueFrom(this.httpd.get(`${this.baseUrl}auth/config`, {
      headers: this.initHeaders
    }));
    return withTimeout(promise, this.initializationTimeout).then(data => this.authProviderStore.next(data));
  }
  async loadUserData() {
    const promise = firstValueFrom(this.httpd.get(`${this.baseUrl}auth/me`, {
      headers: this.initHeaders
    }));
    return withTimeout(promise, this.initializationTimeout).then(data => {
      if (this.loadProfileFrom) {
        if (this.loadProfileFrom.startsWith(this.baseUrl)) {
          this.loadProfileFrom = this.loadProfileFrom.substring(this.baseUrl.length);
        }
        const url = new URL(this.loadProfileFrom, this.baseUrl);
        const profilePromise = firstValueFrom(this.httpd.get(url.toString()));
        return withTimeout(profilePromise, this.initializationTimeout).then(profile => {
          return {
            ...data,
            profile
          };
        });
      } else {
        return Promise.resolve(data);
      }
    }).then(data => {
      this.userDataStore.next(data);
      return true;
    });
  }
  static {
    this.ɵfac = function AuthenticationService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || AuthenticationService)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(i2.Router), i0.ɵɵinject(WINDOW), i0.ɵɵinject(POST_HEADERS), i0.ɵɵinject(BASE_URL, 8), i0.ɵɵinject(DEFAULT_ROUTE, 8), i0.ɵɵinject(LOGIN_ROUTE, 8), i0.ɵɵinject(PUBLIC_ROUTES, 8), i0.ɵɵinject(INITIALIZATION_TIMEOUT, 8), i0.ɵɵinject(INVALIDATE_USER_ON_SESSION_ERROR, 8));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: AuthenticationService,
      factory: AuthenticationService.ɵfac
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AuthenticationService, [{
    type: Injectable
  }], () => [{
    type: i1.HttpClient
  }, {
    type: i2.Router
  }, {
    type: Window,
    decorators: [{
      type: Inject,
      args: [WINDOW]
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: [POST_HEADERS]
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: [BASE_URL]
    }, {
      type: Optional
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: [DEFAULT_ROUTE]
    }, {
      type: Optional
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: [LOGIN_ROUTE]
    }, {
      type: Optional
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: [PUBLIC_ROUTES]
    }, {
      type: Optional
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: [INITIALIZATION_TIMEOUT]
    }, {
      type: Optional
    }]
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: [INVALIDATE_USER_ON_SESSION_ERROR]
    }, {
      type: Optional
    }]
  }], null);
})();
function isAuthenticated(route) {
  return inject(AuthenticationService).isAuthenticated(route);
}
class AuthenticationInterceptor {
  constructor(authService, loginRoute, router, publicRoutes) {
    this.authService = authService;
    this.loginRoute = loginRoute;
    this.router = router;
    this.authProviders = null;
    this.initializationStatus = InitializationStatus.NONE;
    this.publicRoutes = [];
    this.authService.authProviders.subscribe(data => this.authProviders = data);
    this.authService.initializationStatus.subscribe(data => this.initializationStatus = data);
    ensurePromise(publicRoutes, []).then(data => this.publicRoutes = data);
  }
  intercept(req, next) {
    req = req.clone({
      withCredentials: true
    });
    return next.handle(req).pipe(catchError(err => {
      if (err.status === 401 || err.status === 403) {
        const activeRoute = this.router.url;
        if (this.publicRoutes.some(item => typeof item === 'string' ? activeRoute.startsWith(item) : item.test(activeRoute))) {
          return throwError(() => err);
        }
        this.authService.storeActiveRoute(activeRoute);
        const redirectTarget = this.immediateRedirectToAuthProvider();
        if (redirectTarget && this.initializationStatus === InitializationStatus.SUCCESSFUL) {
          setTimeout(() => location.href = redirectTarget);
        } else {
          setTimeout(() => this.router.navigateByUrl(this.loginRoute));
        }
        this.authService.invalidateUser(err.status);
      }
      return throwError(() => err);
    }));
  }
  immediateRedirectToAuthProvider() {
    if (!this.authProviders?.formLogin && this.authProviders?.oauth?.length === 1) {
      return this.authProviders.oauth[0].url || false;
    }
    return false;
  }
  static {
    this.ɵfac = function AuthenticationInterceptor_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || AuthenticationInterceptor)(i0.ɵɵinject(AuthenticationService), i0.ɵɵinject(LOGIN_ROUTE), i0.ɵɵinject(i2.Router), i0.ɵɵinject(PUBLIC_ROUTES));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: AuthenticationInterceptor,
      factory: AuthenticationInterceptor.ɵfac
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AuthenticationInterceptor, [{
    type: Injectable
  }], () => [{
    type: AuthenticationService
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: [LOGIN_ROUTE]
    }]
  }, {
    type: i2.Router
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: [PUBLIC_ROUTES]
    }]
  }], null);
})();
class AuthorizationService {
  constructor(authentication) {
    this.authorizedRoutes = new BehaviorSubject([]);
    this.defaultAuthState = false;
    this.knownRoutes = [];
    this.userData = null;
    authentication.userData.subscribe(data => {
      this.userData = data;
      this.getAuthorizedRoutes(this.knownRoutes);
    });
  }
  getAuthorizedRoutes(routes) {
    if (this.knownRoutes !== routes) {
      this.knownRoutes = routes;
    }
    const result = this.userData ? this.knownRoutes.filter(route => this.isAuthorized(route)) : [];
    this.authorizedRoutes.next(result);
    return result;
  }
  getAuthorizedRoutesAsObservable(routes) {
    this.getAuthorizedRoutes(routes);
    return this.authorizedRoutes.asObservable();
  }
  hasAuthority(role) {
    return this.userData?.authorities.includes(role) ?? false;
  }
  isAuthorized(route) {
    if (!this.userData || !route) {
      return false;
    }
    const config = route.data;
    if (!config?.requiredAuthorizations) {
      return this.defaultAuthState;
    }
    const {
      allOf,
      allowAlways,
      denyAlways,
      oneOf
    } = config.requiredAuthorizations;
    if (denyAlways) {
      return false;
    }
    if (allowAlways) {
      return true;
    }
    if (allOf?.length) {
      const requiredAuthorizations = allOf;
      return requiredAuthorizations.every(auth => this.userData?.authorities.includes(auth));
    }
    if (oneOf?.length) {
      const requiredAuthorizations = oneOf;
      return requiredAuthorizations.some(auth => this.userData?.authorities.includes(auth));
    }
    return this.defaultAuthState;
  }
  setDefautAuthState(value) {
    this.defaultAuthState = value;
  }
  static {
    this.ɵfac = function AuthorizationService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || AuthorizationService)(i0.ɵɵinject(AuthenticationService));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: AuthorizationService,
      factory: AuthorizationService.ɵfac
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(AuthorizationService, [{
    type: Injectable
  }], () => [{
    type: AuthenticationService
  }], null);
})();
const isAuthorized = route => {
  return inject(AuthorizationService).isAuthorized(route);
};
class KDOAuthenticationModule {
  static forRoot(params) {
    return {
      ngModule: KDOAuthenticationModule,
      providers: [{
        provide: WINDOW,
        useValue: window
      }, {
        provide: BASE_URL,
        useValue: params.baseUrl
      }, {
        provide: DEFAULT_ROUTE,
        useValue: params.defaultRoute ?? '/'
      }, {
        provide: INITIALIZATION_TIMEOUT,
        useValue: params.initializationTimeout ?? 5000
      }, {
        provide: INVALIDATE_USER_ON_SESSION_ERROR,
        useValue: params.invalidateUserOnSessionError ?? false
      }, {
        provide: LOGIN_ROUTE,
        useValue: params.loginRoute
      }, {
        provide: POST_HEADERS,
        useValue: params.additionalPostHeaders ?? {}
      }, {
        provide: PUBLIC_ROUTES,
        useValue: params.publicRoutes ?? []
      }, AuthenticationService, AuthorizationService, {
        provide: HTTP_INTERCEPTORS,
        useClass: AuthenticationInterceptor,
        multi: true
      }]
    };
  }
  static {
    this.ɵfac = function KDOAuthenticationModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || KDOAuthenticationModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: KDOAuthenticationModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(KDOAuthenticationModule, [{
    type: NgModule,
    args: [{}]
  }], null, null);
})();

/*
Das Passwort muss folgende Kriterien erfüllen:
- ein oder mehr Großbuchstaben enthalten.
- ein oder mehr Ziffern enthalten.
- ein oder mehr Sonderzeichen enthalten.
- mindestens 12 Zeichen lang sein.
*/
const PASSWORD_PATTERN = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[^\w]).{12,}$/;

/*
 * Public API Surface of auth
 */

/**
 * Generated bundle index. Do not edit.
 */

export { ACTIVE_ROUTE_KEY, AuthenticationInterceptor, AuthenticationService, AuthorizationService, BASE_URL, DEFAULT_ROUTE, ERROR_AUTHENTICATION_NOT_INITALIZED, INITIALIZATION_TIMEOUT, INVALIDATE_USER_ON_SESSION_ERROR, InitializationStatus, KDOAuthenticationModule, LOGIN_ROUTE, OtpConfiguration, PASSWORD_PATTERN, POST_HEADERS, PUBLIC_ROUTES, isAuthenticated, isAuthorized };
